import React from "react";
import {
  Box,
  Grid,
  Typography,
  Stack,
  Chip,
  Button,
  Card,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Star as StarIcon,
  ZoomIn as ZoomIcon,
} from "@mui/icons-material";
import ProgressPhotoGroupReviewNotes from "./ProgressPhotoGroupReviewNotes";
import ProgressPhotosPrivacyGate from "../molecules/ProgressPhotosPrivacyGate";
import { getProgressPhotoTypeChipColor } from "../../utils/progressPhotoTypeColor";

const PhotoCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  overflow: "hidden",
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "scale(1.03)",
  },
  cursor: "pointer",
}));

function formatGroupDate(date) {
  if (!date) return "Unknown date";
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "Unknown date";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Unified progress photo group: header, thumbnails, coach notes.
 * Photo images are hidden until reveal unless requireReveal={false}
 * (e.g. parent already wrapped with ProgressPhotosPrivacyGate).
 */
const ProgressPhotoGroupPanel = ({
  group,
  clientId,
  isReferenceGroup = false,
  showCoachNotes = true,
  showHeaderActions = false,
  onSetReference,
  onDeleteGroup,
  variant = "default",
  onPhotoClick,
  photoCardSx,
  /** When true (default), images stay hidden until coach reveals this group */
  requireReveal = true,
}) => {
  if (!group?.photos?.length) return null;

  const isDense = variant === "dense";
  const gridMd = isDense ? 2 : 4;
  const gridSm = isDense ? 3 : 6;
  const gridXs = isDense ? 4 : 12;
  const dateVariant = isDense ? "subtitle2" : "h6";
  const imgMaxHeight = isDense ? 120 : undefined;

  const photoGrid = (
    <Grid container spacing={isDense ? 1 : 2}>
      {group.photos.map((photo) => (
        <Grid item xs={gridXs} sm={gridSm} md={gridMd} key={photo.id}>
          <PhotoCard
            onClick={onPhotoClick ? () => onPhotoClick(photo) : undefined}
            sx={{
              ...(typeof photoCardSx === "function"
                ? photoCardSx(photo)
                : photoCardSx),
              ...(isDense
                ? {
                    "&:hover": { transform: "scale(1.02)" },
                  }
                : {}),
            }}
          >
            <Box sx={{ position: "relative" }}>
              <img
                src={photo.url}
                alt={`Progress photo - ${photo.displayName || photo.type}`}
                loading="lazy"
                style={{
                  width: "100%",
                  height: imgMaxHeight ? imgMaxHeight : "auto",
                  maxHeight: imgMaxHeight || "none",
                  objectFit: imgMaxHeight ? "cover" : undefined,
                  display: "block",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: isDense ? 4 : 8,
                  right: isDense ? 4 : 8,
                  zIndex: 1,
                }}
              >
                <Chip
                  label={photo.displayName || photo.type}
                  color={getProgressPhotoTypeChipColor(photo.type)}
                  size={isDense ? "small" : "small"}
                  sx={isDense ? { height: 22, fontSize: "0.7rem" } : undefined}
                />
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background:
                    "linear-gradient(transparent, rgba(0,0,0,0.75))",
                  color: "white",
                  p: isDense ? 0.5 : 1,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    sx={
                      isDense
                        ? { fontSize: "0.65rem", lineHeight: 1.2 }
                        : undefined
                    }
                    noWrap
                  >
                    {photo.displayName || photo.type}
                  </Typography>
                  {!isDense ? <ZoomIcon sx={{ fontSize: 18 }} /> : null}
                </Stack>
              </Box>
            </Box>
          </PhotoCard>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: isDense ? 1 : 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            variant={dateVariant}
            color="text.primary"
            fontWeight={isDense ? 600 : undefined}
          >
            {formatGroupDate(group.date)}
          </Typography>
          {isReferenceGroup ? (
            <Chip
              icon={<StarIcon />}
              label="Reference Photos"
              color="primary"
              size="small"
              sx={{ fontWeight: "bold" }}
            />
          ) : null}
        </Stack>
        {showHeaderActions && (onSetReference || onDeleteGroup) ? (
          <Stack direction="row" spacing={1}>
            {onSetReference ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => onSetReference(group.groupId)}
              >
                Set as Reference
              </Button>
            ) : null}
            {onDeleteGroup ? (
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => onDeleteGroup(group.groupId, group.date)}
                sx={{
                  "&:hover": {
                    backgroundColor: "error.light",
                    color: "error.contrastText",
                  },
                }}
              >
                Delete Group
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Stack>

      {requireReveal ? (
        <ProgressPhotosPrivacyGate
          resetKey={`${clientId || ""}-${group.groupId}`}
          title="Photos hidden"
          description="Reveal to view this submission."
          revealLabel="Reveal photos"
          hideLabel="Hide photos"
        >
          {photoGrid}
        </ProgressPhotosPrivacyGate>
      ) : (
        photoGrid
      )}

      {showCoachNotes && clientId && group.groupId ? (
        <ProgressPhotoGroupReviewNotes
          clientId={clientId}
          groupId={group.groupId}
        />
      ) : null}
    </Box>
  );
};

export default ProgressPhotoGroupPanel;
